import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { APPT_TYPE, APPT_STATUS } from "@/lib/constants";
import { toast } from "sonner";

export default function NewAppointmentDialog({ open, onOpenChange, defaultClientId, defaultDate, onCreated }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: defaultClientId || "",
    starts_at: defaultDate || "",
    duration_minutes: 60,
    type: "seans",
    location: "online",
    status: "planlandi",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => setClients(r.data));
    setForm((prev) => ({
      ...prev,
      client_id: defaultClientId || prev.client_id,
      starts_at: defaultDate || prev.starts_at,
    }));
    // eslint-disable-next-line
  }, [open, defaultClientId, defaultDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Danışan seçin");
      return;
    }
    setBusy(true);
    try {
      // Convert local datetime-local input to ISO UTC-ish
      const startsIso = form.starts_at
        ? new Date(form.starts_at).toISOString()
        : new Date().toISOString();
      const { data } = await api.post("/appointments", { ...form, starts_at: startsIso });
      toast.success("Randevu oluşturuldu");
      onCreated && onCreated(data);
      onOpenChange(false);
      setForm({
        client_id: defaultClientId || "",
        starts_at: "",
        duration_minutes: 60,
        type: "seans",
        location: "online",
        status: "planlandi",
        notes: "",
      });
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-appointment-dialog">
        <DialogHeader>
          <DialogTitle>Yeni Randevu</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Danışan *</label>
            <select
              required
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              data-testid="new-appt-client"
              className="w-full h-9 px-2 border border-pleiades-border rounded-sm text-[13px] bg-white focus:outline-none focus:border-pleiades-blue"
            >
              <option value="">Seçin…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Başlangıç *</label>
            <input
              type="datetime-local"
              required
              value={form.starts_at}
              onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              data-testid="new-appt-starts"
              className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Süre (dk)</label>
              <input
                type="number"
                min={15}
                step={15}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value || "0", 10) })}
                data-testid="new-appt-duration"
                className="w-full h-9 px-3 border border-pleiades-border rounded-sm text-[13px] focus:outline-none focus:border-pleiades-blue"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Tür</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="new-appt-type"
                className="w-full h-9 px-2 border border-pleiades-border rounded-sm text-[13px] bg-white"
              >
                {Object.entries(APPT_TYPE).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Konum</label>
              <select
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="new-appt-location"
                className="w-full h-9 px-2 border border-pleiades-border rounded-sm text-[13px] bg-white"
              >
                <option value="online">Online</option>
                <option value="ofis">Ofis</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-pleiades-slate mb-1">Not</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full text-[13px] border border-pleiades-border rounded-sm p-2 focus:outline-none focus:border-pleiades-blue resize-none"
            />
          </div>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="h-9 px-3 border border-pleiades-border rounded-sm text-[13px] hover:bg-pleiades-sidebar">
              Vazgeç
            </button>
            <button type="submit" disabled={busy} data-testid="new-appt-submit" className="h-9 px-3 bg-pleiades-blue hover:bg-pleiades-blue-hover text-white text-[13px] rounded-sm disabled:opacity-50">
              {busy ? "Kaydediliyor…" : "Randevu Oluştur"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
